import { http, delay, HttpResponse, passthrough } from "msw";
import type { paths } from "@/services/customer-schema";
import type { paths as policyPaths } from "@/services/policy-schema";
import type { paths as pathsV1 } from "@/services/kustodian-customer-api-schema";
import { accounts, accountArrayForEachType } from "./accounts.mock";
import { getAuthDomain } from "@/services/auth";
import {
  accountAssetResponse,
  accountAssetsResponse,
  assetsResponse,
  allocationsResponse,
} from "./assets.mock";
import { policiesResponse, policyResponse } from "./policies.mock";
import { approvalResponse } from "./approvals.mock";
import { kioskSettingsResponse } from "./kiosk.mock";
import { depositAddresses, depositAddress } from "./depositAddresses.mock";
import {
  withdrawalAddressesResponse,
  withdrawalAddressResponse,
} from "./withdrawlAddresses.mock";
import { transactions } from "./transactions.mock";
import { faker } from "@faker-js/faker";
import { generateMockApproval } from "./approvals.mock";
import { type MockMode } from "@/services/mockConfig";
import { userPermissions } from "./user.mock";
import { assetTransactions } from "./assetTransactions.mock";
import {
  beneficiariesUpsertResponse,
  beneficiariesV2FirstPage,
  beneficiariesV2SecondPage,
  beneficiariesV2SearchResult,
  beneficiariesGetResponse,
} from "./beneficiary.mock";
import { valuation } from "@/mocks/valuation.mock.ts";

faker.seed(0);
const fakeDelay = async () => await delay(100);

/**
 * Mock API Routes
 */
export const mockApi = [
  /**
   * Ping (keep alive)
   */
  http.get(`*/ping`, async () => {
    return HttpResponse.json({ status: "ok", data: "pong" });
  }),

  /**
   * Policies
   */
  http.get(`*/v2/policies`, async () => {
    await fakeDelay();

    return HttpResponse.json({
      ...policiesResponse,
    });
  }),

  http.patch("*/v2/policies/:policyId", async ({ request }) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const body: any = await request.json();
    await fakeDelay();

    const updatedPolicyResponse = {
      ...policyResponse,
      settings: [
        {
          ...policyResponse.settings[0],
          requiredApprovers: body.requiredApprovers,
        },
      ],
    };

    return HttpResponse.json({
      ...updatedPolicyResponse,
    });
  }),
];

/**
 * Mixed Mode (Real) API Routes
 */
export const realApi = [
  /**
   * Approvals
   */
  http.get(`*/v2/approvals`, async ({ request }) => {
    const url = new URL(request.url);
    const responseLength = 10;
    const query = url.searchParams;
    const approvals = Array.from({ length: responseLength }, () =>
      generateMockApproval(query),
    );

    await fakeDelay();

    return HttpResponse.json<
      policyPaths["/v2/approvals"]["get"]["responses"]["200"]["content"]["application/json"]
    >({
      instances: approvals,
      pagination: {
        total: responseLength,
        limit: responseLength,
        nextCursor: null,
        prevCursor: null,
        hasMore: false,
      },
    });
  }),

  http.get("*/v2/approvals/:approvalId", async ({ params }) => {
    const { approvalId } = params;

    await fakeDelay();

    return HttpResponse.json({
      ...approvalResponse,
      id: approvalId,
    });
  }),

  http.patch("*/v2/approvals/:approvalId", async () => {
    await fakeDelay();

    return HttpResponse.json({
      ...approvalResponse,
    });
  }),

  /**
   * Accounts
   */
  http.get(`*/v2/accounts`, async () => {
    await fakeDelay();
    return HttpResponse.json(accounts);
  }),

  http.get(`*/v2/valuation`, async () => {
    await fakeDelay();
    return HttpResponse.json(valuation);
  }),

  http.get(`*/v2/accounts/:accountId`, async ({ params }) => {
    const { accountId } = params;

    const example = accountArrayForEachType.find(
      (account) => account.id === accountId,
    );

    await fakeDelay();
    return HttpResponse.json(example);
  }),

  http.get(`*/v2/accounts/:accountId/assets`, async () => {
    // const { accountId } = params;

    const example = accountAssetsResponse;

    await fakeDelay();
    return HttpResponse.json(example);
  }),

  http.post(
    `*/v2/accounts/:accountId/validate`,
    async ({ params, request }) => {
      const { accountId } = params;
      const body =
        (await request.json()) as paths["/v2/accounts/{accountId}/validate"]["post"]["requestBody"]["content"]["application/json"];

      await fakeDelay();

      if (body.name === "partial") {
        return HttpResponse.json({ match: "PARTIAL_MATCH" });
      } else if (
        accounts.accounts.some((account) => account.id === accountId)
      ) {
        return HttpResponse.json({ match: "EXACT_MATCH" });
      } else {
        return HttpResponse.json({ match: "NO_MATCH" });
      }
    },
  ),

  http.get(`*/v2/accounts/:accountId/assets/:assetId`, async () => {
    // const { accountId, assetId } = params;

    const example = accountAssetResponse;

    await fakeDelay();
    return HttpResponse.json(example);
  }),

  /**
   * User Permissions
   */
  http.get(`*/v2/permissions`, async () => {
    await fakeDelay();

    return HttpResponse.json({
      permissions: userPermissions,
    });
  }),

  /**
   * Global Assets
   */
  http.get(`*/v2/assets`, async () => {
    await fakeDelay();

    return HttpResponse.json({
      ...assetsResponse,
    });
  }),

  /**
   * Global Allocations
   */
  http.get(`*/v2/allocations`, async () => {
    await fakeDelay();

    return HttpResponse.json({
      ...allocationsResponse,
    });
  }),

  /**
   * Deposit Addresses
   */
  http.get(`*/v2/addresses`, async () => {
    await fakeDelay();
    return HttpResponse.json(depositAddresses);
  }),

  http.post(`*/v2/addresses`, async () => {
    await fakeDelay();
    return HttpResponse.json(depositAddress);
  }),

  /**
   * Withdrawal Addresses
   */
  http.get(`*/v2/external-addresses`, async () => {
    await fakeDelay();
    return HttpResponse.json({
      ...withdrawalAddressesResponse,
    });
  }),

  http.post(`*/v2/external-addresses`, async () => {
    await fakeDelay();
    return HttpResponse.json<
      paths["/v2/external-addresses"]["post"]["responses"]["201"]["content"]["application/json"]
    >({
      id: faker.string.alphanumeric(16),
      status: "PENDING",
    });
  }),

  // V1 Withdrawal Addresses
  http.post(`*/v1/withdrawals/addresses`, async () => {
    await fakeDelay();
    return HttpResponse.json<
      pathsV1["/v1/withdrawals/addresses"]["post"]["responses"]["201"]
    >({
      headers: {},
    });
  }),

  http.delete(`*/v2/external-addresses/:addressId`, async ({ params }) => {
    await fakeDelay();
    const { addressId } = params;

    const updatedAddress = withdrawalAddressesResponse.addresses.find(
      (address) => address.id === addressId,
    );
    if (updatedAddress) {
      updatedAddress.status = "withdrawn";
    }

    return HttpResponse.json<
      paths["/v2/external-addresses/{addressId}"]["delete"]["responses"]["200"]["content"]["application/json"]
    >({
      id: faker.string.alphanumeric(16),
      status: "WITHDRAWN",
    });
  }),

  http.get(`*/v2/external-addresses/:addressId`, async () => {
    await fakeDelay();

    return HttpResponse.json<
      paths["/v2/external-addresses/{addressId}"]["get"]["responses"]["200"]["content"]["application/json"]
    >({
      ...withdrawalAddressResponse,
    });
  }),

  /**
   * Transactions
   */
  http.get(`*/v2/transactions`, async ({ request }) => {
    const url = new URL(request.url);
    const accountId = url.searchParams.get("accountId");
    const assetId = url.searchParams.get("assetId");
    const type = url.searchParams.get("type");
    const source = url.searchParams.get("source");

    await fakeDelay();

    if (accountId && assetId) {
      return HttpResponse.json({
        ...transactions,
        entries: assetTransactions,
      });
    }

    let filteredEntries = transactions.entries;

    if (accountId && !assetId) {
      // Find the account name from the accountId
      const matchedAccount = accounts.accounts.find(
        (acc) => acc.id === accountId,
      );

      if (matchedAccount) {
        const accountName = matchedAccount.name;

        filteredEntries = transactions.entries.filter((transaction) => {
          const senderMatch = transaction.sender?.account === accountName;
          const recipientMatch = transaction.recipient?.account === accountName;
          return senderMatch || recipientMatch;
        });
      } else {
        // No matching account ID, return empty array or original data?
        filteredEntries = []; // or keep as transactions.entries
      }
    }

    filteredEntries = filteredEntries.filter((transaction) => {
      const typeMatch = !type || transaction.type === type;
      const sourceMatch = !source || transaction.transactionType === source;
      return typeMatch && sourceMatch;
    });

    return HttpResponse.json({
      ...transactions,
      entries: filteredEntries,
    });
  }),

  /**
   * make a transfer
   */
  http.post(`*/v2/transfers`, async () => {
    await fakeDelay();
    return HttpResponse.json(
      {
        id: faker.string.alphanumeric(16),
        status: "COMPLETED",
      },
      {
        status: 201,
      },
    );
  }),

  /**
   * make a withdrawal
   */
  http.post(`*/v2/withdrawals`, async () => {
    await fakeDelay();
    return HttpResponse.json<
      paths["/v2/withdrawals"]["post"]["responses"]["201"]["content"]["application/json"]
    >({
      id: faker.string.alphanumeric(16),
      status: "PENDING",
    });
  }),

  http.delete(`*/v2/withdrawals/:requestId`, async ({ params }) => {
    await fakeDelay();
    const { requestId } = params;

    return HttpResponse.json<
      paths["/v2/withdrawals/{requestId}"]["delete"]["responses"]["200"]["content"]["application/json"]
    >({
      id: requestId as string,
      status: "CANCELLED",
    });
  }),

  /**
   * Kiosk Settings
   */
  http.get(`*/v1/accounts/:accountId/kiosk-settings`, async () => {
    await fakeDelay();

    return HttpResponse.json<
      pathsV1["/v1/accounts/{accountId}/kiosk-settings"]["get"]["responses"]["200"]["content"]["application/json"]
    >({
      ...kioskSettingsResponse,
    });
  }),

  http.post(`*/v1/accounts/:accountId/kiosk-settings`, async () => {
    await fakeDelay();

    return new HttpResponse(null, { status: 204 });
  }),
  http.get(`*/v2/personal-information/:accountId`, async ({ request }) => {
    await fakeDelay();
    const url = new URL(request.url);
    const query = url.searchParams;

    const cursor = query.get("cursor");
    const search = query.get("search");
    const resultsToReturn = search
      ? beneficiariesV2SearchResult
      : cursor == "secondPage"
        ? beneficiariesV2SecondPage
        : beneficiariesV2FirstPage;
    return HttpResponse.json<
      paths["/v2/personal-information/{accountId}"]["get"]["responses"]["200"]["content"]["application/json"]
    >({
      ...resultsToReturn,
    });
  }),
  http.put(`*/v1/personal-information/:accountId/:clientRef`, async () => {
    await fakeDelay();

    return HttpResponse.json(beneficiariesUpsertResponse);
  }),
  http.get(`*/v1/personal-information/:accountId/:clientRef`, async () => {
    await fakeDelay();

    return HttpResponse.json(beneficiariesGetResponse);
  }),
];

const getAuthPassthroughHandlers = () => {
  const authDomain = getAuthDomain();
  if (!authDomain) {
    return [];
  }

  return [
    http.all(`https://${authDomain}/*`, () => passthrough()),
    http.all(`http://${authDomain}/*`, () => passthrough()),
  ];
};

/**
 * Handle which handlers to mock
 * @param mockMode 'full | mixed'
 * @returns array of hanlders to mock
 */
export const getHandlers = (mockMode: MockMode) => {
  const authPassthrough = getAuthPassthroughHandlers();

  switch (mockMode) {
    case "full":
      return [...authPassthrough, ...mockApi, ...realApi];
    case "mixed":
      return [...authPassthrough, ...mockApi];
    default:
      // don't intercept any routes
      return [];
  }
};
